<template>
  <div class="container-fluid py-3">
    <!-- Header -->
    <div class="d-flex align-items-center gap-2 mb-3">
      <h5 class="mb-0">SKU Wise Sales — Detail View</h5>
    </div>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-2">
          <div class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">Distributor</label>
            <select
              v-model="selectedDistributor"
              class="form-select form-select-sm"
              @change="fetchData"
              :disabled="isDistributor"
            >
              <option v-if="!isDistributor" value="">All Distributors</option>
              <option v-if="isDistributor" :value="profileId">
                {{ loggedUserName }}
              </option>
              <option
                v-for="d in distributors"
                :key="d.profile_id"
                :value="d.profile_id"
              >
                {{ d.name }}
              </option>
            </select>
          </div>

          <div class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">Dealer Type</label>
            <select
              v-model="selectedDealerType"
              class="form-select form-select-sm"
              @change="fetchData"
            >
              <option value="">All Dealers</option>
              <option
                v-for="type in dealerTypeList"
                :key="type.id"
                :value="type.id"
              >
                {{ type.name }}
              </option>
            </select>
          </div>

          <div class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">From Date</label>
            <input
              type="date"
              v-model="selectedFromDate"
              class="form-control form-control-sm"
              @change="fetchData"
            />
          </div>

          <div class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">To Date</label>
            <input
              type="date"
              v-model="selectedToDate"
              class="form-control form-control-sm"
              @change="fetchData"
            />
          </div>

          <div class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">SKU</label>
            <!-- <select v-model="selectedSku" class="form-select form-select-sm" @change="fetchData">
                            <option value="">All SKUs</option>
                            <option v-for="item in itemsList" :key="item.id" :value="item.id">
                                {{ item.item_name }}
                            </option>
                        </select> -->
            <select
              v-model="selectedSku"
              class="form-select form-select-sm"
              @change="handleSkuChange"
            >
              <option value="">All SKUs</option>
              <option v-for="item in itemsList" :key="item.id" :value="item.id">
                {{ item.item_name || item.description }}
              </option>
            </select>
          </div>

          <div class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">Measure</label>
            <!-- <select
              v-model="selectedMeasure"
              class="form-select form-select-sm"
            >
              <option value="mt">Volume (MT)</option>
              <option value="qty">Quantity (Qty)</option>
            </select> -->
            <select
              v-model="selectedMeasure"
              class="form-select form-select-sm"
              @change="handleMeasureChange"
            >
              <option v-if="!isSelectedSkuQtyOnly" value="mt">
                Volume (MT)
              </option>

              <option value="qty">Quantity (Qty)</option>
            </select>
          </div>

          <div class="col-sm-6 col-md-2 d-flex align-items-end">
            <button
              class="btn btn-sm btn-outline-secondary w-100"
              @click="exportExcel"
            >
              <i class="fa fa-download me-1"></i> Export Excel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-content-center py-5">
      <div class="spinner-border text-primary"></div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5 text-muted">
      <img
        src="../../../../assets/vectors/data_not_found.png"
        alt="No data"
        style="height: 80px"
        class="mb-2 d-block mx-auto"
      />
      {{ error }}
    </div>

    <!-- Table -->
    <!-- <div v-else-if="tableData.length" class="card"> -->
    <div v-else-if="tableData.length" class="card sku-detail-table-card">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <span class="fw-semibold">
          {{ selectedFromDate }} to {{ selectedToDate }} — Distributor / Route /
          Dealer Breakdown
        </span>
        <span class="text-muted small">
          {{ totalDealers }} dealers · {{ items.length }} SKUs · showing
          {{ selectedMeasure === "mt" ? "Metric Tons" : "Quantity" }}
        </span>
      </div>

      <div class="card-body p-0">
        <!-- <div style="overflow-x: auto"> -->
        <div class="table-responsive sku-detail-table-scroll">
          <table
            class="table table-bordered table-hover table-sm mb-0 sku-detail-table"
          >
            <thead class="sticky-thead">
              <tr>
                <th class="col-distributor">Distributor</th>
                <th class="col-route">Route</th>
                <th class="col-dealer">Dealer</th>
                <th class="text-end col-total">Invoice Value (LKR)</th>
                <th class="text-end col-total">
                  Total Item
                  {{ selectedMeasure === "mt" ? "Volume" : "Quantity" }}
                </th>
                <template v-for="item in items" :key="item.item_id">
                  <th class="text-end col-item" :title="item.item_name">
                    {{ item.item_name }}
                  </th>
                  <th
                    class="text-end col-item-value"
                    :title="item.item_name + ' — Invoice Value'"
                  >
                    {{ item.item_name }} (Val)
                  </th>
                </template>
              </tr>
            </thead>

            <tbody>
              <template v-for="dist in tableData" :key="dist.distributor_id">
                <template v-for="route in dist.routes" :key="route.route_id">
                  <tr
                    v-for="(dealer, dIdx) in route.dealers"
                    :key="dealer.dealer_id"
                  >
                    <!-- Distributor cell — rowspan across all routes+dealers -->
                    <td
                      v-if="isFirstRowOfDist(dist, route, dIdx)"
                      :rowspan="distRowspan(dist)"
                      class="align-middle dist-cell"
                    >
                      <div class="dist-name">{{ dist.distributor_name }}</div>
                      <div class="dist-totals mt-1">
                        <span class="dist-total-badge">
                          Total: {{ fmt(distTotal(dist)) }}
                        </span>
                      </div>
                    </td>

                    <!-- Route cell — rowspan across dealers in this route -->
                    <td
                      v-if="dIdx === 0"
                      :rowspan="route.dealers.length"
                      class="align-middle route-cell"
                    >
                      <div class="route-name">{{ route.route_name }}</div>
                      <div class="dist-totals mt-1">
                        <span class="dist-total-badge">
                          Total: {{ fmt(routeTotal(route)) }}
                        </span>
                      </div>
                    </td>

                    <!-- Dealer -->
                    <td class="align-middle dealer-cell">
                      {{ dealer.dealer_name }}
                    </td>

                    <td class="text-end align-middle" style="color: #0984e3">
                      {{ formatCurrency(dealer.invoice_value) }}
                    </td>

                    <!-- Row total for this dealer -->
                    <td class="text-end align-middle fw-semibold">
                      {{ fmt(dealerTotal(dealer)) }}
                    </td>

                    <!-- One cell per item + its invoice value -->
                    <template v-for="item in items" :key="item.item_id">
                      <td
                        class="text-end align-middle"
                        :class="!dealer.items[item.item_id] ? 'text-muted' : ''"
                      >
                        {{
                          dealer.items[item.item_id]
                            ? fmt(valueOf(dealer.items[item.item_id]))
                            : "—"
                        }}
                      </td>
                      <td
                        class="text-end align-middle"
                        :class="!dealer.items[item.item_id] ? 'text-muted' : ''"
                        style="color: #0984e3"
                      >
                        {{
                          dealer.items[item.item_id]
                            ? formatCurrency(
                                dealer.items[item.item_id].item_invoice_value,
                              )
                            : "—"
                        }}
                      </td>
                    </template>
                  </tr>
                </template>
              </template>
            </tbody>

            <!-- Grand Total footer -->
            <tfoot class="sticky-tfoot">
              <tr class="table-dark fw-semibold">
                <td colspan="3" style="color: #fff">Grand Total</td>
                <td class="text-end" style="color: #fff">
                  {{ formatCurrency(grandInvoiceTotal()) }}
                </td>
                <td class="text-end" style="color: #fff">
                  {{ fmt(grandTotal()) }}
                </td>
                <template v-for="item in items" :key="item.item_id">
                  <td class="text-end" style="color: #fff">
                    {{ fmt(grandItemTotal(item.item_id)) }}
                  </td>
                  <td class="text-end" style="color: #fff">
                    {{ formatCurrency(grandItemInvoiceTotal(item.item_id)) }}
                  </td>
                </template>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  fetchDistributors,
  fetchItemNameList,
  fetchBusinessTypesList,
  fetchSkuWiseSalesDetails,
} from "@/services/dataService";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default {
  name: "SkuWiseSalesDetail",

  data() {
    const q = this.$route?.query || {};
    return {
      MONTHS,
      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      isDistributor: localStorage.getItem("user_label") === "DISTRIBUTOR",
      loggedUserName: localStorage.getItem("business_name"),

      loading: false,
      error: null,

      distributors: [],
      itemsList: [],
      dealerTypeList: [],

      selectedDistributor: q.name || "",
      selectedFromDate: q.from_date || new Date().toISOString().split("T")[0],
      selectedToDate: q.to_date || new Date().toISOString().split("T")[0],
      selectedSku: q.sku || "",
      selectedDealerType: "",
      selectedMeasure: q.measure || "mt",

      months: MONTHS,

      items: [],
      tableData: [],
    };
  },

  mounted() {
    if (this.isDistributor) {
      this.selectedName = this.profileId;
    } else {
      this.loadDistributors();
    }
    this.loadItemsList();
    this.loadDealerTypes();
    this.fetchData();
  },

  computed: {
    yearOptions() {
      const years = [];
      for (let y = new Date().getFullYear(); y >= 2000; y--) years.push(y);
      return years;
    },

    totalDealers() {
      return this.tableData.reduce(
        (s, d) => s + d.routes.reduce((rs, r) => rs + r.dealers.length, 0),
        0,
      );
    },

    selectedSkuItem() {
      if (!this.selectedSku) return null;

      return this.itemsList.find(
        (item) =>
          String(item.id ?? item.variant_id) === String(this.selectedSku),
      );
    },

    isSelectedSkuQtyOnly() {
      if (!this.selectedSku) return false;

      return (
        Number(this.selectedSkuItem?.is_cylinder) === 0 ||
        this.selectedSkuItem?.allowed_measures === "qty"
      );
    },
  },

  methods: {
    // ── Data loading ──────────────────────────────────────────────────────

    async loadDistributors() {
      try {
        const res = await fetchDistributors(this.profileId, this.token);
        this.distributors = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },

    // async loadItemsList() {
    //   try {
    //     const companyId = localStorage.getItem("company_id");
    //     const res = await fetchItemNameList(companyId, 1, this.token);
    //     this.itemsList = res.data.data;
    //   } catch (e) {
    //     console.error(e);
    //   }
    // },

    async loadItemsList() {
      try {
        const companyId = localStorage.getItem("company_id");
        const res = await fetchItemNameList(companyId, 1, this.token);

        this.itemsList = res.data?.data || [];
        this.applySkuMeasureRule();
      } catch (e) {
        console.error(e);
      }
    },

    async loadDealerTypes() {
      try {
        const res = await fetchBusinessTypesList(this.token);
        this.dealerTypeList = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },

    async fetchData() {
      try {
        this.loading = true;
        this.error = null;
        this.tableData = [];
        this.items = [];

        const payload = {
          profile_id: this.selectedDistributor || this.profileId,
          from_date: this.selectedFromDate,
          to_date: this.selectedToDate,
          sku_id: this.selectedSku || "",
          type_id: this.selectedDealerType || "",
          measure: this.selectedMeasure,
        };

        const res = await fetchSkuWiseSalesDetails(payload, this.token);

        if (res.data.success && res.data.data.length > 0) {
          this.items = res.data.items;
          this.tableData = res.data.data;
        } else {
          this.error = "No data available for the selected filters.";
        }
      } catch (e) {
        console.error(e);
        this.error = "Something went wrong. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    applySkuMeasureRule() {
      if (this.isSelectedSkuQtyOnly) {
        this.selectedMeasure = "qty";
      }
    },

    handleSkuChange() {
      this.applySkuMeasureRule();
      this.fetchData();
    },

    handleMeasureChange() {
      this.applySkuMeasureRule();
      this.fetchData();
    },

    // ── Value helpers ─────────────────────────────────────────────────────

    valueOf(itemCell) {
      if (!itemCell) return 0;
      return this.selectedMeasure === "mt"
        ? itemCell.total_metric_tons || 0
        : itemCell.total_qty || 0;
    },

    // Format a number
    fmt(val) {
      if (!val) return "—";
      return this.selectedMeasure === "mt"
        ? parseFloat(val).toFixed(3)
        : Math.round(val).toLocaleString();
    },

    // Total for one dealer row
    dealerTotal(dealer) {
      return Object.values(dealer.items).reduce(
        (s, cell) => s + this.valueOf(cell),
        0,
      );
    },

    // Total for a route
    routeTotal(route) {
      return route.dealers.reduce((s, d) => s + this.dealerTotal(d), 0);
    },

    // Total for a distributor
    distTotal(dist) {
      return dist.routes.reduce((s, r) => s + this.routeTotal(r), 0);
    },

    formatCurrency(val) {
      if (!val) return "—";
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val);
    },

    grandInvoiceTotal() {
      return this.tableData.reduce(
        (s, dist) =>
          s +
          dist.routes.reduce(
            (rs, route) =>
              rs +
              route.dealers.reduce(
                (ds, dealer) => ds + (dealer.invoice_value || 0),
                0,
              ),
            0,
          ),
        0,
      );
    },

    // Grand total across everything
    grandTotal() {
      return this.tableData.reduce((s, d) => s + this.distTotal(d), 0);
    },

    // Grand total for one specific item column
    grandItemTotal(itemId) {
      let total = 0;
      this.tableData.forEach((dist) => {
        dist.routes.forEach((route) => {
          route.dealers.forEach((dealer) => {
            total += this.valueOf(dealer.items[itemId]);
          });
        });
      });
      return total;
    },

    grandItemInvoiceTotal(itemId) {
      let total = 0;
      this.tableData.forEach((dist) => {
        dist.routes.forEach((route) => {
          route.dealers.forEach((dealer) => {
            total += dealer.items[itemId]?.item_invoice_value || 0;
          });
        });
      });
      return total;
    },

    // ── Rowspan helpers ───────────────────────────────────────────────────

    // Total dealer rows for a distributor
    distRowspan(dist) {
      return dist.routes.reduce((s, r) => s + r.dealers.length, 0);
    },

    // Show the distributor cell only on the very first row of the first route
    isFirstRowOfDist(dist, route, dIdx) {
      return dist.routes[0].route_id === route.route_id && dIdx === 0;
    },

    // ── Export ────────────────────────────────────────────────────────────

    exportExcel() {
      const measureLabel = this.selectedMeasure === "mt" ? "MT" : "Qty";
      const distName =
        this.distributors.find((d) => d.profile_id === this.selectedDistributor)
          ?.name || "All Distributors";
      const monthName = MONTHS[this.selectedMonth - 1];

      // Metadata rows
      const filterRows = [
        [`SKU Wise Sales Detail — ${measureLabel}`],
        [],
        ["Distributor", distName],
        ["Period", `${this.selectedFromDate} to ${this.selectedToDate}`],
        [],
      ];

      // Header row
      const headerRow = [
        "Distributor",
        "Route",
        "Dealer",
        `Total (${measureLabel})`,
        ...this.items.map((i) => `${i.item_name} (${measureLabel})`),
      ];

      const totalCols = headerRow.length;
      const pad = (arr) => {
        const out = [...arr];
        while (out.length < totalCols) out.push("");
        return out.slice(0, totalCols);
      };

      // Data rows
      const dataRows = [];
      this.tableData.forEach((dist) => {
        let distWritten = false;
        dist.routes.forEach((route) => {
          let routeWritten = false;
          route.dealers.forEach((dealer) => {
            dataRows.push(
              pad([
                distWritten ? "" : dist.distributor_name,
                routeWritten ? "" : route.route_name,
                dealer.dealer_name,
                this.fmt(this.dealerTotal(dealer)),
                ...this.items.map((item) =>
                  dealer.items[item.item_id]
                    ? this.fmt(this.valueOf(dealer.items[item.item_id]))
                    : "",
                ),
              ]),
            );
            distWritten = true;
            routeWritten = true;
          });

          // Route subtotal
          dataRows.push(
            pad([
              "",
              `${route.route_name} — Subtotal`,
              "",
              this.fmt(this.routeTotal(route)),
              ...this.items.map((item) => {
                const t = route.dealers.reduce(
                  (s, d) => s + this.valueOf(d.items[item.item_id]),
                  0,
                );
                return t ? this.fmt(t) : "";
              }),
            ]),
          );
        });

        // Distributor subtotal
        dataRows.push(
          pad([
            `${dist.distributor_name} — Subtotal`,
            "",
            "",
            this.fmt(this.distTotal(dist)),
            ...this.items.map((item) => {
              const t = dist.routes.reduce(
                (s, r) =>
                  s +
                  r.dealers.reduce(
                    (rs, d) => rs + this.valueOf(d.items[item.item_id]),
                    0,
                  ),
                0,
              );
              return t ? this.fmt(t) : "";
            }),
          ]),
        );
      });

      // Grand total row
      const grandTotalRow = pad([
        "Grand Total",
        "",
        "",
        this.fmt(this.grandTotal()),
        ...this.items.map((item) =>
          this.fmt(this.grandItemTotal(item.item_id)),
        ),
      ]);

      // Footer
      const firstName = localStorage.getItem("first_name") || "";
      const lastName = localStorage.getItem("last_name") || "";
      const footerRows = [
        [],
        ["Generated by", (firstName + " " + lastName).trim() || "-"],
        ["Generated at", new Date().toLocaleString("en-US", { hour12: false })],
      ];

      // Assemble
      const aoa = [
        ...filterRows.map(pad),
        pad(headerRow),
        ...dataRows,
        grandTotalRow,
        ...footerRows.map(pad),
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = [
        { wch: 26 },
        { wch: 22 },
        { wch: 26 },
        { wch: 14 },
        ...this.items.map(() => ({ wch: 16 })),
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "SKU Wise Sales");
      XLSX.writeFile(
        wb,
        `SKU_Wise_Sales_${this.selectedFromDate}_to_${this.selectedToDate}.xlsx`,
      );
    },
  },
};
</script>

<style scoped>
.sku-detail-table th,
.sku-detail-table td {
  white-space: nowrap;
  font-size: 0.82rem;
}

.col-distributor {
  min-width: 180px;
}
.col-route {
  min-width: 150px;
}
.col-dealer {
  min-width: 180px;
}
.col-total {
  min-width: 100px;
}
.col-item {
  min-width: 120px;
}

.dist-cell {
  background-color: #f0f4ff;
  vertical-align: middle !important;
}

.dist-name {
  font-weight: 600;
  font-size: 0.85rem;
  color: #2d3a8c;
}

.route-cell {
  background-color: #f7f9fc;
  vertical-align: middle !important;
}

.route-name {
  font-weight: 500;
  font-size: 0.83rem;
  color: #444;
}

.dealer-cell {
  color: #333;
}

.dist-totals {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.dist-total-badge {
  font-size: 0.72rem;
  background: #dde4ff;
  color: #2d3a8c;
  border-radius: 4px;
  padding: 1px 5px;
  white-space: nowrap;
}

/* .sticky-thead th {
  position: sticky;
  top: 0;
  z-index: 20;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  color: #334155;
  border-bottom: 1px solid #dbe3ef;
  box-shadow: 0 2px 0 rgba(15, 23, 42, 0.06);
  font-weight: 700;
  vertical-align: middle;
} */

.sticky-thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  color: #334155;
  border-bottom: 1px solid #dbe3ef;
  box-shadow: 0 2px 0 rgba(15, 23, 42, 0.06);
  font-weight: 700;
  vertical-align: middle;
}

/* .sticky-tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 18; */

.sticky-tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 3;
  background: #111827 !important;
  color: #ffffff !important;
  border-color: #1f2937 !important;
  border-bottom: 0 !important;

  /* covers tiny white gap above horizontal scrollbar */
  box-shadow: 0 3px 0 #111827 !important;
}

/* Inner scroll after around 15 rows */
.sku-detail-table-scroll {
  max-height: 500px;
  overflow: auto;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  border-radius: 0 0 12px 12px;
  background: #ffffff;
  scrollbar-gutter: stable;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.78) #f1f5f9;

  box-shadow: inset -10px 0 14px -14px rgba(15, 23, 42, 0.32),
    inset 0 -10px 14px -14px rgba(15, 23, 42, 0.32);
}

/* Chrome / Edge / Safari */
.sku-detail-table-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.sku-detail-table-scroll::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 999px;
}

.sku-detail-table-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
  border-radius: 999px;
  border: 2px solid #f1f5f9;
  min-height: 42px;
  min-width: 42px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.18);
}

.sku-detail-table-scroll::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #64748b 0%, #475569 100%);
  border-color: #e2e8f0;
}

.sku-detail-table-scroll::-webkit-scrollbar-thumb:active {
  background: linear-gradient(180deg, #475569 0%, #334155 100%);
}

.sku-detail-table-scroll::-webkit-scrollbar-corner {
  background: #f1f5f9;
}

/* Required for many SKU columns */
.sku-detail-table {
  min-width: max-content;
  margin-bottom: 0 !important;
  border-collapse: collapse !important;
  background: #ffffff;
}

.sku-detail-table th,
.sku-detail-table td {
  white-space: nowrap;
  border-color: #e8edf4 !important;
}

/* Smooth row hover */
.sku-detail-table tbody tr {
  transition: background-color 0.18s ease;
}

.sku-detail-table tbody tr:hover {
  background-color: #f8fafc;
}

/* Responsive height */
@media (max-width: 768px) {
  .sku-detail-table-scroll {
    max-height: 72vh;
  }

  .sku-detail-table th,
  .sku-detail-table td {
    font-size: 12px;
  }
}

.sticky-tfoot tr {
  border-bottom: 0 !important;
}

.sku-detail-table {
  margin-bottom: 0 !important;
}

.sku-detail-table-scroll {
  background: #ffffff !important;
}

/* make scrollbar track look connected, not like a white empty gap */
.sku-detail-table-scroll::-webkit-scrollbar-track:horizontal {
  background: #e2e8f0;
}

.sku-detail-table-scroll::-webkit-scrollbar-corner {
  background: #e2e8f0;
}

/* Prevent sticky table header from overlapping dashboard top bar */
.sku-detail-table-card {
  position: relative;
  z-index: 1;
  overflow: hidden;
  isolation: isolate;
}

.sku-detail-table-scroll {
  position: relative;
  z-index: 1;
  isolation: isolate;
}
</style>
